import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../style/Services.css";
import Footer from "../components/Footer";
import Header from "../components/Header";

const Services = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".sv-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="sv-root">
      <Header />

      {/* ── HERO ───────────────────────────────────────── */}
      <section className="sv-hero">
        <div className="sv-hero__content">
          <div className="sv-hero__accent" />
          <span className="sv-eyebrow">{t("services.heroEyebrow")}</span>
          <h1 className="sv-hero__title">{t("services.heroTitle")}</h1>
          <p className="sv-hero__para">{t("services.heroText")}</p>
          <div className="sv-hero__actions">
            <button
              type="button"
              className="sv-btn sv-btn--gold"
              onClick={() => navigate("/contact")}
            >
              {t("services.bookConsultation")}
            </button>
            <button
              type="button"
              className="sv-btn sv-btn--outline"
              onClick={() => navigate("/services")}
            >
              {t("services.viewServices")}
            </button>
          </div>
        </div>
        <div className="sv-hero__scroll">
          <span className="sv-hero__scroll-line" />
        </div>
      </section>

      {/* ── INTRO ──────────────────────────────────────── */}
      <section className="sv-intro">
        <div className="sv-intro__inner sv-reveal">
          <div className="sv-section-head">
            <span className="sv-eyebrow sv-eyebrow--dark">{t("services.offerEyebrow")}</span>
            <h2 className="sv-section-title">{t("services.offerTitle")}</h2>
          </div>
          <p className="sv-intro__text">{t("services.offerText")}</p>
        </div>
      </section>

      {/* ── SERVICE CARDS ──────────────────────────────── */}
      <section className="sv-cards">
        {t("services.cards", { returnObjects: true }).map((item, i) => (
          <div
            className={`sv-card sv-reveal ${item.dark ? "sv-card--dark" : ""}`}
            key={item.number}
            style={{ transitionDelay: `${i * 0.08}s` }}
          >
            <span className="sv-card__num">{item.number}</span>
            <h3 className="sv-card__title">{item.title}</h3>
            <p className="sv-card__text">{item.text}</p>
          </div>
        ))}
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────── */}
      <section className="sv-process">
        <div className="sv-process__head sv-reveal">
          <span className="sv-eyebrow">{t("services.processEyebrow")}</span>
          <h2 className="sv-section-title sv-section-title--light">
            {t("services.processTitle")}
          </h2>
          <p className="sv-process__sub">{t("services.processText")}</p>
        </div>

        <div className="sv-process__track" aria-hidden="true" />

        <div className="sv-process__grid">
          {t("services.steps", { returnObjects: true }).map((step, i) => (
            <div
              className="sv-step sv-reveal"
              key={step.number}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <div className="sv-step__circle">{step.number}</div>
              <h3 className="sv-step__title">{step.title}</h3>
              <p className="sv-step__text">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURE ────────────────────────────────────── */}
      <section className="sv-feature">
        <div className="sv-feature__image sv-reveal">
          <img
            src="https://images.unsplash.com/photo-1542362567-b07e54358753?auto=format&fit=crop&w=1200&q=80"
            alt={t("services.featureImageAlt")}
          />
        </div>
        <div className="sv-feature__content sv-reveal" style={{ transitionDelay: "0.14s" }}>
          <div className="sv-section-head">
            <span className="sv-eyebrow sv-eyebrow--dark">{t("services.privateEyebrow")}</span>
            <h2 className="sv-section-title">{t("services.privateTitle")}</h2>
          </div>
          <p className="sv-feature__text">{t("services.privateText")}</p>
          <button
            type="button"
            className="sv-btn sv-btn--gold"
            onClick={() => navigate("/contact")}
          >
            {t("services.startJourney")}
          </button>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────── */}
      <section className="sv-cta">
        <div className="sv-cta__inner sv-reveal">
          <div className="sv-cta__text">
            <span className="sv-eyebrow">{t("services.ctaEyebrow")}</span>
            <h2 className="sv-cta__title">{t("services.ctaTitle")}</h2>
            <p className="sv-cta__sub">{t("services.ctaText")}</p>
          </div>
          <button
            type="button"
            className="sv-btn sv-btn--gold sv-cta__btn"
            onClick={() => navigate("/contact")}
          >
            {t("services.scheduleAppointment")}
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Services;
